import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { List, Box, TextField, Autocomplete, InputAdornment, Grid, Button, Typography, CircularProgress, Chip } from '@mui/material';
import { Clear, Search } from '@mui/icons-material';
import EditableListItem from './AmplifiersListItem';
import Helper from '../providers/Helpers';
import amplifiersProvider from '../providers/AmplifiersProvider';

const AmplifiersList = forwardRef(({ groups, onEditingStateChange, isDrawerOpen }, ref) => {
    const [isLoading, setIsLoading] = useState(true);
    const [amplifiers, setAmplifiers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [filteredAmplifiers, setFilteredAmplifiers] = useState([]);
    const [editingItems, setEditingItems] = useState(new Set());
    const [showEditingOnly, setShowEditingOnly] = useState(false);
    const [globalLoading, setGlobalLoading] = useState(false);
    const [loadingItemId, setLoadingItemId] = useState(null);

    const sortAmplifiers = (amplifiers) => {
        // Separa os amplificadores especiais
        const central = amplifiers.find(amp => amp.name === 'Central');
        const modem = amplifiers.find(amp => amp.name === 'Modem');
        
        // Filtra os demais amplificadores
        const others = amplifiers.filter(amp => 
            amp.name !== 'Central' && amp.name !== 'Modem'
        );
        
        // Combina na ordem desejada
        return [
            ...(central ? [central] : []),
            ...(modem ? [modem] : []),
            ...others
        ];
    };

    const handleSearch = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        applyFilters(amplifiers, term, selectedGroup, statusFilter, showEditingOnly);
    };

    const handleGroupFilter = (event, group) => {
        setSelectedGroup(group);
        applyFilters(amplifiers, searchTerm, group, statusFilter, showEditingOnly);
    };

    const handleStatusFilter = (event) => {
        const newStatus = event.target.value;
        setStatusFilter(newStatus);
        applyFilters(amplifiers, searchTerm, selectedGroup, newStatus, showEditingOnly);
    };

    const handleEditingFilterToggle = () => {
        const newShowEditingOnly = !showEditingOnly;
        setShowEditingOnly(newShowEditingOnly);
        applyFilters(amplifiers, searchTerm, selectedGroup, statusFilter, newShowEditingOnly);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedGroup(null);
        setStatusFilter('all');
        setShowEditingOnly(false);
        setFilteredAmplifiers(amplifiers);
    };

    const handleItemEditingChange = useCallback((itemId, isEditing) => {
        setEditingItems(prev => {
            const newSet = new Set(prev);
            if (isEditing) {
                newSet.add(itemId);
            } else {
                newSet.delete(itemId);
            }
            onEditingStateChange(newSet.size > 0);
            return newSet;
        });
    }, [onEditingStateChange]);

    const handleItemDelete = useCallback((serialNumber) => {
        // Atualiza o estado local removendo o item excluído
        setAmplifiers(current => 
            sortAmplifiers(current.filter(amp => amp.serialNumber !== serialNumber))
        );
        // Força atualização dos filtros
        applyFilters(
            amplifiers.filter(amp => amp.serialNumber !== serialNumber),
            searchTerm,
            selectedGroup,
            statusFilter,
            showEditingOnly
        );
    }, [amplifiers, searchTerm, selectedGroup, statusFilter, showEditingOnly]);

    // Função simplificada de filtro
    const applyFilters = (items, term, group, status, showEditing) => {
        let filtered = [...items];

        if (showEditing) {
            filtered = filtered.filter(amp => editingItems.has(amp.serialNumber));
        }

        if (term) {
            filtered = filtered.filter(amp => 
                amp.serialNumber.toLowerCase().includes(term.toLowerCase()) ||
                amp.name.toLowerCase().includes(term.toLowerCase())
            );
        }

        if (group) {
            filtered = filtered.filter(amp => amp.groupId === group.vL_CADASTRO);
        }

        if (status && status !== 'all') {
            if (status === 'notracked') {
                filtered = filtered.filter(amp => amp.isNotTracked());
            } else {
                filtered = filtered.filter(amp => amp.status === status && !amp.isNotTracked());
            }
        }

        setFilteredAmplifiers(filtered);
    };

    const updateState = (state) => {
        setAmplifiers(sortAmplifiers(state.amplifiers));
    };

    // Atualiza filtros quando os amplifiers mudarem
    useEffect(() => {
        setIsLoading(false);
        applyFilters(amplifiers, searchTerm, selectedGroup, statusFilter, showEditingOnly);
    }, [amplifiers]);

    useEffect(() => {
        // Plano B, utilizar o amplifiersProvider.getAllAmplifiers para popular o array quando o isDrawerOpen abrir
        if (isDrawerOpen) {
            let amplifiersList = amplifiersProvider.getAllAmplifiers();
            setAmplifiers(sortAmplifiers(amplifiersList));
        }
    }, [isDrawerOpen]);

    // useEffect(() => {
    //     amplifiersProvider.subscribe(updateState);
    //     return () => amplifiersProvider.unsubscribe(updateState);
    // }, []);

    useImperativeHandle(ref, () => ({
        resetEditing: () => {
            setEditingItems(new Set());
            onEditingStateChange(false);
        }
    }), [onEditingStateChange]);

    return (
        <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={10}>
                    <Grid container spacing={2}>
                        {/* Busca por texto */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                value={searchTerm}
                                onChange={handleSearch}
                                placeholder="Pesquisar ID ou Nome..."
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Filtro de Grupo */}
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                fullWidth
                                value={selectedGroup}
                                onChange={handleGroupFilter}
                                options={groups.sort((a, b) => {
                                    const groupNumberA = parseInt(a.vL_CADASTRO.slice(12), 10);
                                    const groupNumberB = parseInt(b.vL_CADASTRO.slice(12), 10);
                                    return groupNumberA - groupNumberB;
                                })}
                                size="small"
                                getOptionLabel={(option) => `${option.vL_CADASTRO.slice(12)} - ${option.dS_LOCALIZACAO || 'Sem nome registrado'}`}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filtrar por Grupo"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Filtro de Status */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                fullWidth
                                value={statusFilter}
                                onChange={handleStatusFilter}
                                label="Status"
                                size="small"
                                SelectProps={{
                                    native: true
                                }}
                            >
                                <option value="all">Todos</option>
                                <option value="online">Online</option>
                                <option value="offline">Offline</option>
                                <option value="buscando">Sincronizando</option>
                                <option value="notracked">Não Mapeados</option>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                                label={`${editingItems.size} item(s) em edição`}
                                color={showEditingOnly ? "info" : "default"}
                                onClick={handleEditingFilterToggle}
                                sx={{
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}  md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Clear />}
                        onClick={handleClearFilters}
                        fullWidth
                        size="medium"
                        disabled={(!searchTerm && !selectedGroup && statusFilter === 'all') || editingItems.size > 0}
                        sx={{ paddingInline: 0 }}
                    >
                        Limpar Filtros
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ 
                overflow: 'auto', 
                flex: 1, 
                maxHeight: 'calc(100vh - 220px)', 
                height: "100%", 
                paddingInline: 1, 
                paddingY: 0.1, 
                backgroundColor: 'rgba(92,92,92,0.1)', 
                border: "2px solid #383838", 
                borderRadius: "4px",
                display: 'flex',
                justifyContent: 'center',
                alignItems: isLoading || filteredAmplifiers.length === 0 ? 'center' : 'flex-start' 
            }}>
                {isLoading ? (
                    <CircularProgress color="secondary" />
                ) : filteredAmplifiers.length > 0 ? (
                    <List sx={{ width: '100%' }}>
                        {filteredAmplifiers.map((amplifier) => (
                            <EditableListItem
                                key={amplifier.serialNumber}
                                amplifier={amplifier}
                                groups={groups}
                                onDelete={handleItemDelete}
                                onEditingChange={(isEditing) =>
                                    handleItemEditingChange(amplifier.serialNumber, isEditing)
                                }
                                globalLoading={globalLoading}
                                setGlobalLoading={setGlobalLoading}
                                loadingItemId={loadingItemId}
                                setLoadingItemId={setLoadingItemId}
                            />
                        ))}
                    </List>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4
                    }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Nenhum equipamento encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm || selectedGroup || statusFilter !== 'all' 
                                ? 'Tente modificar os filtros de busca'
                                : 'Não há equipamentos cadastrados'}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                        Exibindo {filteredAmplifiers.length} de {amplifiersProvider.getAllAmplifiers().length} dispositivos
                        {searchTerm || selectedGroup || statusFilter !== 'all' ? ' (filtrados)' : ''}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    {process.env.REACT_APP_MODE === 'desktop' ? (
                        <Typography textAlign="end" variant="body2">v{process.env.REACT_APP_BUILD_VERSION}</Typography>
                    ) : null}
                </Grid>
            </Grid>
        </>
    );
});

// Adiciona displayName para melhor debug
AmplifiersList.displayName = 'AmplifiersList';

export default AmplifiersList;